import { parsePhaseGuide, type EquipmentCategory } from "@/lib/schema/phaseGuide";
import type { Tables } from "@/types/database";

export function taskSourceText(task: Pick<
  Tables<"tasks">,
  "title" | "description" | "description_summary" | "notes"
>): string {
  const summary = task.description_summary?.trim();
  const description = task.description?.trim();
  const notes = task.notes?.trim();
  const body = summary || description || "";
  return [task.title.trim(), body, notes ? `Jegyzetek:\n${notes}` : ""]
    .filter(Boolean)
    .join("\n\n");
}

export function phaseSourceText(
  phase: Pick<Tables<"phases">, "title" | "description" | "guide" | "status">
): string {
  const guide = parsePhaseGuide(phase.guide);
  const sections: Array<[string, string]> = [
    ["Cél", guide.goal],
    ["Munkafolyamat", guide.workflow],
    ["Figyelmeztetések", guide.cautions],
    ["Időtartam", guide.duration],
    ["Eszközök", guide.equipment.narrative],
    ["Anyagok", guide.materials],
    ["Ellenőrzőlista", guide.checklist],
    ["Műszaki részletek", guide.technical_details],
    ["Gyakori hibák", guide.common_mistakes],
    ["Projektre szabott", guide.project_specific_notes]
  ];
  const guideMd = sections
    .filter(([, v]) => v.trim())
    .map(([h, v]) => `## ${h}\n${v.trim()}`)
    .join("\n\n");
  return [phase.title.trim(), `Státusz: ${phase.status}`, phase.description?.trim() ?? "", guideMd]
    .filter(Boolean)
    .join("\n\n");
}

const EQUIPMENT_CATEGORY_LABEL: Record<EquipmentCategory, string> = {
  material: "Anyagok",
  machine: "Gépek, bérelhető eszközök",
  hand_tool: "Kéziszerszámok",
  ppe: "Védőeszközök",
  other: "Egyéb"
};

const EQUIPMENT_CATEGORY_ORDER: EquipmentCategory[] = [
  "material",
  "machine",
  "hand_tool",
  "ppe",
  "other"
];

/**
 * A fázis-útmutatók strukturált anyag- és eszköztételei. Külön chunkban, hogy a
 * „mi kell a zsaluzáshoz?" kérdésre konkrét tétellistával lehessen válaszolni.
 */
export function phaseMaterialsSourceText(
  phase: Pick<Tables<"phases">, "title" | "guide">
): string {
  const guide = parsePhaseGuide(phase.guide);
  const items = guide.equipment.items;
  if (items.length === 0) return "";

  const groups = EQUIPMENT_CATEGORY_ORDER.map((cat) => {
    const rows = items.filter((i) => (i.category ?? "other") === cat);
    if (rows.length === 0) return "";
    const lines = rows.map((i) => `- ${i.name}${i.note ? ` — ${i.note}` : ""}`).join("\n");
    return `## ${EQUIPMENT_CATEGORY_LABEL[cat]}\n${lines}`;
  }).filter(Boolean);

  return [
    `Anyagok és eszközök: ${phase.title.trim()}`,
    `${items.length} tétel a fázis útmutatójából.`,
    ...groups
  ].join("\n\n");
}

export function dailyLogSourceText(
  log: Pick<
    Tables<"site_daily_logs">,
    "log_date" | "weather_summary" | "work_summary" | "issues"
  >,
  taskTitle?: string | null
): string {
  const lines = [
    `Helyszíni napló — ${log.log_date}`,
    taskTitle ? `Feladat: ${taskTitle}` : "",
    log.weather_summary?.trim() ? `Időjárás: ${log.weather_summary.trim()}` : "",
    log.work_summary?.trim() ? `Elvégzett munka:\n${log.work_summary.trim()}` : "",
    log.issues?.trim() ? `Problémák:\n${log.issues.trim()}` : ""
  ];
  return lines.filter(Boolean).join("\n\n");
}

const EXPENSE_CATEGORY_LABEL: Record<string, string> = {
  material: "anyag",
  labor: "munkadíj",
  service: "szolgáltatás",
  permit: "engedély",
  equipment: "eszköz",
  other: "egyéb"
};

export function expenseCategoryLabel(category: string | null | undefined): string {
  if (!category) return "nincs kategória";
  return EXPENSE_CATEGORY_LABEL[category] ?? category;
}

export type ExpenseSummaryRow = Pick<
  Tables<"expenses">,
  "title" | "amount" | "date" | "category" | "notes"
>;

function sumAmounts(rows: ExpenseSummaryRow[]): number {
  return rows.reduce((acc, r) => acc + (Number(r.amount) || 0), 0);
}

function money(n: number): string {
  return `${Math.round(n).toLocaleString("hu-HU")} EUR`;
}

/**
 * Kiadás-összesítő chunk. Nem soronként indexelünk, mert az elhígítaná a
 * keresést; egy bucket (fázis vagy hónap) egy chunk.
 */
export function expenseSummarySourceText(args: {
  label: string;
  rows: ExpenseSummaryRow[];
  itemLimit?: number;
}): string {
  const rows = [...args.rows].sort((a, b) => b.date.localeCompare(a.date));
  if (rows.length === 0) return "";
  const limit = args.itemLimit ?? 40;

  const byCategory = new Map<string, ExpenseSummaryRow[]>();
  for (const r of rows) {
    const key = expenseCategoryLabel(r.category);
    const list = byCategory.get(key);
    if (list) list.push(r);
    else byCategory.set(key, [r]);
  }
  const categoryLines = [...byCategory.entries()]
    .sort((a, b) => sumAmounts(b[1]) - sumAmounts(a[1]))
    .map(([label, list]) => `- ${label}: ${money(sumAmounts(list))} (${list.length} tétel)`)
    .join("\n");

  const byMonth = new Map<string, ExpenseSummaryRow[]>();
  for (const r of rows) {
    const key = r.date.slice(0, 7);
    const list = byMonth.get(key);
    if (list) list.push(r);
    else byMonth.set(key, [r]);
  }
  const monthLine = [...byMonth.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([ym, list]) => `${ym}: ${money(sumAmounts(list))}`)
    .join(" · ");

  const itemLines = rows
    .slice(0, limit)
    .map(
      (r) =>
        `- ${r.date} · ${r.title} · ${money(Number(r.amount) || 0)} (${expenseCategoryLabel(r.category)})`
    )
    .join("\n");
  const truncated = rows.length > limit ? `\n- … további ${rows.length - limit} tétel` : "";

  return [
    `Kiadás-összesítő: ${args.label}`,
    `Összesen ${money(sumAmounts(rows))}, ${rows.length} tétel.`,
    `Kategóriák:\n${categoryLines}`,
    `Havi bontás: ${monthLine}`,
    `Tételek:\n${itemLines}${truncated}`
  ].join("\n\n");
}

const QUOTE_STATUS_LABEL: Record<string, string> = {
  pending: "elbírálás alatt",
  accepted: "elfogadva",
  rejected: "elutasítva",
  expired: "lejárt"
};

export function quoteSourceText(
  quote: Pick<
    Tables<"quotes">,
    "title" | "amount" | "currency" | "status" | "valid_until" | "notes"
  >,
  refs?: { contactName?: string | null; taskTitle?: string | null }
): string {
  const amount =
    quote.amount === null || quote.amount === undefined
      ? ""
      : `${Math.round(Number(quote.amount)).toLocaleString("hu-HU")} ${quote.currency ?? "EUR"}`;
  return [
    `Árajánlat: ${quote.title.trim()}`,
    quote.status ? `Státusz: ${QUOTE_STATUS_LABEL[quote.status] ?? quote.status}` : "",
    amount ? `Összeg: ${amount}` : "",
    quote.valid_until ? `Érvényes: ${quote.valid_until}` : "",
    refs?.contactName ? `Ajánlatadó: ${refs.contactName}` : "",
    refs?.taskTitle ? `Feladat: ${refs.taskTitle}` : "",
    quote.notes?.trim() ? `Megjegyzés:\n${quote.notes.trim()}` : ""
  ]
    .filter(Boolean)
    .join("\n");
}

const CONTACT_TYPE_LABEL: Record<string, string> = {
  contractor: "kivitelező",
  supplier: "beszállító",
  designer: "tervező",
  authority: "hatóság",
  other: "egyéb"
};

export function contactSourceText(
  contact: Pick<
    Tables<"contacts">,
    "name" | "company" | "type" | "trade" | "phone" | "email" | "notes"
  >
): string {
  return [
    `Kontakt: ${contact.name.trim()}`,
    contact.company?.trim() ? `Cég: ${contact.company.trim()}` : "",
    contact.type ? `Típus: ${CONTACT_TYPE_LABEL[contact.type] ?? contact.type}` : "",
    contact.trade?.trim() ? `Szakma: ${contact.trade.trim()}` : "",
    contact.phone?.trim() ? `Telefon: ${contact.phone.trim()}` : "",
    contact.email?.trim() ? `E-mail: ${contact.email.trim()}` : "",
    contact.notes?.trim() ? `Jegyzet:\n${contact.notes.trim()}` : ""
  ]
    .filter(Boolean)
    .join("\n");
}

export const FACT_KIND_LABEL: Record<string, string> = {
  decision: "döntés",
  preference: "preferencia",
  constraint: "megkötés",
  supplier: "beszállító",
  measurement: "méret / mennyiség",
  issue: "probléma",
  change_log: "változásnapló",
  rfq: "ajánlatkérés",
  supplier_ban: "tiltott beszállító",
  other: "egyéb"
};

export function factSourceText(
  fact: {
    kind: string | null;
    statement: string;
    details?: string | null;
    valid_from?: string | null;
  },
  refs?: { phaseTitle?: string | null; taskTitle?: string | null }
): string {
  return [
    `Projekt-tény (${FACT_KIND_LABEL[fact.kind ?? "other"] ?? "egyéb"}): ${fact.statement.trim()}`,
    fact.details?.trim() ? fact.details.trim() : "",
    refs?.phaseTitle ? `Fázis: ${refs.phaseTitle}` : "",
    refs?.taskTitle ? `Feladat: ${refs.taskTitle}` : "",
    fact.valid_from ? `Érvényes ettől: ${fact.valid_from}` : ""
  ]
    .filter(Boolean)
    .join("\n");
}
